"use client";

import { memo } from "react";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DisclaimerTone = "danger" | "warning" | "info";

export interface DisclaimerItem {
  id: string;
  tone: DisclaimerTone;
  icon: string; // iconify icon name, e.g. "mdi:alert"
  title: string;
  description?: string;
  href?: string; // optional link target; when provided, the pill becomes a link
}

interface DisclaimerBoxProps {
  items: DisclaimerItem[];
  className?: string;
}

const toneClasses: Record<DisclaimerTone, { wrapper: string; text: string }> = {
  danger: { wrapper: "bg-red-900/20", text: "text-red-300" },
  warning: { wrapper: "bg-amber-900/20", text: "text-amber-300" },
  info: { wrapper: "bg-blue-900/20", text: "text-blue-300" },
};

const DisclaimerBox = memo(function DisclaimerBox({ items, className }: DisclaimerBoxProps) {
  const { t } = useTranslation("common");

  if (!items || items.length === 0) return null;

  // Mobile view (below lg breakpoint) - single button with count
  const MobileView = () => (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={t("disclaimerBox.viewDisclaimers", { count: items.length })}
          className="bg-accent-foreground/10 text-accent-foreground flex h-8 items-center gap-2 rounded-lg border-none px-2"
          type="button"
        >
          <span className="flex items-center justify-center">
            <Icon height="0.9rem" icon="mdi:information-outline" width="0.9rem" />
          </span>
          <span className="text-xs">
            {t("disclaimerBox.viewDisclaimers", { count: items.length })}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" side="bottom" sideOffset={6}>
        <div className="space-y-3">
          <h4 className="font-medium">{t("disclaimerBox.disclaimers")}</h4>
          {items.map((item) => {
            const colors = toneClasses[item.tone];

            return (
              <div key={item.id} className="space-y-1">
                {item.href ? (
                  <Link className="block" to={item.href}>
                    <div className={cn("flex items-start gap-2 rounded-lg p-2", colors.wrapper)}>
                      <span className="mt-0.5 flex items-center justify-center">
                        <Icon className={colors.text} height="1rem" icon={item.icon} width="1rem" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className={cn("text-sm font-medium", colors.text)}>{item.title}</div>
                        {item.description && (
                          <p className={cn("mt-1 text-xs opacity-80", colors.text)}>
                            {item.description}
                          </p>
                        )}
                      </div>
                      <span className="mt-0.5 flex items-center justify-center">
                        <Icon
                          className={colors.text}
                          height="0.8rem"
                          icon="mdi:open-in-new"
                          width="0.8rem"
                        />
                      </span>
                    </div>
                  </Link>
                ) : (
                  <div className={cn("flex items-start gap-2 rounded-lg p-2", colors.wrapper)}>
                    <span className="mt-0.5 flex items-center justify-center">
                      <Icon className={colors.text} height="1rem" icon={item.icon} width="1rem" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={cn("text-sm font-medium", colors.text)}>{item.title}</div>
                      {item.description && (
                        <p className={cn("mt-1 text-xs opacity-80", colors.text)}>
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );

  // Desktop view (lg+ breakpoint) - individual pills
  const DesktopView = () => (
    <div className="flex max-w-full flex-nowrap items-center gap-2">
      {items.map((item) => {
        const colors = toneClasses[item.tone];

        return (
          <div key={item.id}>
            {item.href ? (
              <Link
                aria-label={item.description ? `${item.title} - ${item.description}` : item.title}
                className={cn(
                  "flex h-8 max-w-[50vw] flex-nowrap items-center gap-2 rounded-lg border-none px-2",
                  colors.wrapper,
                  colors.text,
                )}
                to={item.href}
              >
                <span className="flex items-center justify-center">
                  <Icon height="0.9rem" icon={item.icon} width="0.9rem" />
                </span>
                <span className={cn("text-xs", "truncate")}>{item.title}</span>
                {item.description ? (
                  <span className={cn("hidden truncate text-xs opacity-80 2xl:inline")}>
                    {item.description}
                  </span>
                ) : null}
              </Link>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      "flex h-8 max-w-[50vw] flex-nowrap items-center gap-2 rounded-lg border-none px-2",
                      colors.wrapper,
                      colors.text,
                    )}
                  >
                    <span className="flex items-center justify-center">
                      <Icon height="0.9rem" icon={item.icon} width="0.9rem" />
                    </span>
                    <span className={cn("text-xs", "truncate")}>{item.title}</span>
                    {item.description ? (
                      <span className={cn("hidden truncate text-xs opacity-80 xl:inline")}>
                        {item.description}
                      </span>
                    ) : null}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6}>
                  <div className="max-w-xs">
                    <div className="mb-1 flex items-center gap-2">
                      <Icon height="1rem" icon={item.icon} width="1rem" />
                      <span className="font-medium">{item.title}</span>
                    </div>
                    {item.description ? (
                      <p className="text-muted-foreground text-xs">{item.description}</p>
                    ) : null}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <TooltipProvider>
      <motion.div
        animate={{ opacity: 1, x: 0 }}
        className={cn("mx-2 flex items-center gap-2 overflow-hidden", className)}
        initial={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Mobile view below lg breakpoint */}
        <div className="lg:hidden">
          <MobileView />
        </div>

        {/* Desktop view lg+ breakpoint */}
        <div className="hidden lg:block">
          <DesktopView />
        </div>
      </motion.div>
    </TooltipProvider>
  );
});

//@ts-ignore
export default DisclaimerBox;
